/**
 * NostoAutoComplete component
 *
 * Handles predictive search functionality using Nosto's API with Shopify fallback
 */
class NostoAutoComplete extends HTMLElement {
  constructor() {
    super();
    // Cache storage
    this.cachedResults = {};

    // Core element references
    this.parentDrawer = this.closest('#drawer-search');
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.inlineCloseButton = this.querySelector('[data-inline-search-close]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.loadingText = this.getAttribute('data-loading-text');

    // Initialize skeleton references
    this.skeletonLoaders = this.querySelectorAll('.loading-skeleton-search');
    this.resultLists = this.querySelectorAll('.predictive-search__results-list');

    this.context = 'autocomplete';

    // State tracking
    this.isOpen = false;
    this.searchTerm = '';
    this.abortController = new AbortController();

    // Options with fallback handling
    this.options = JSON.parse(this.getAttribute('data-options') || '{}');
    this.showCategories = this.options.showCategories !== false;
    this.maxKeywords = this.options.maxKeywords !== undefined ? parseInt(this.options.maxKeywords) : 3;
    this.maxRetries = 3;

    // Add redirect URL storage
    this.redirectUrl = null;
    // Disallow premature form submitting with Enter key
    this.enableFormSubmit = false;

    this.initEventListeners();

    // Store bound method for later cleanup
    this.boundHandleProductClick = this.handleProductClick.bind(this);
  }

  /**
   * Set up all event listeners
   */
  initEventListeners() {
    // Input and form events
    if (this.input) {
      this.input.form?.addEventListener('reset', this.onFormReset.bind(this));
      this.input.form?.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('input', this.debounce(this.onChange.bind(this), 300));
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    this.inlineCloseButton?.addEventListener('pointerdown', this.onInlineClosePointerDown.bind(this));
    this.inlineCloseButton?.addEventListener('click', this.onInlineClose.bind(this));

    // Keyboard navigation and Escape to close
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Close when clicking outside the component
    this.boundOnDocumentClick = this.onDocumentClick.bind(this);
    document.addEventListener('click', this.boundOnDocumentClick);

    // Initialize Nosto recommendations when drawer opens
    document.addEventListener('modal:opened', (e) => {
      if (this.parentDrawer && e.detail?.modalId === this.parentDrawer.id) {
        initNostoRecommendations();
        this.getSearchResults(this.getQuery());
      }
    });

  }

  /**
   * Clean up when element is removed from DOM
   */
  disconnectedCallback() {
    // Abort any pending fetches
    if (this.abortController) {
      this.abortController.abort();
    }

    // Remove event listeners
    this.input?.form?.removeEventListener('reset', this.onFormReset);
    this.input?.form?.removeEventListener('submit', this.onFormSubmit);
    this.input?.removeEventListener('input', this.onChange);
    this.input?.removeEventListener('focus', this.onFocus);

    // Remove document click listener
    if (this.boundOnDocumentClick) {
      document.removeEventListener('click', this.boundOnDocumentClick);
    }

    // Remove click tracking if it exists
    const productsList = this.querySelector('#predictive-search-results-products-list');
    if (productsList) {
      productsList.removeEventListener('click', this.boundHandleProductClick);
    }
  }

  /**
   * Set up click tracking for product cards using event delegation
   */
  setupClickTracking() {
    const productsList = this.querySelector('#predictive-search-results-products-list');
    if (productsList) {
      // Remove any existing listener first to avoid duplicates
      productsList.removeEventListener('click', this.boundHandleProductClick);
      // Add the new listener
      productsList.addEventListener('click', this.boundHandleProductClick);
    }
  }

  /**
   * Handle click events on product cards and track via Nosto API
   * @param {Event} event - Click event
   */
  handleProductClick(event) {
    // Find the closest product card element
    const card = event.target.closest('.predictive-search__item--link-with-thumbnail');
    if (!card) return;

    // Extract product data from the clicked card
    const productUrl = card.getAttribute('href');
    if (!productUrl) return;

    // Extract product ID from URL or use fallback attribute
    const productId = card.getAttribute('data-product-id');
    if (productId && productUrl) {
      // Track the click through Nosto API
      nostojs(api => {
        api.recordSearchClick(
          this.context,
          {
            productId: productId,
            url: productUrl
          }
        );
      });

    }
  }

  /**
   * Get the current search query
   * @returns {string} The trimmed search query
   */
  getQuery() {
    return this.input.value.trim();
  }

  /**
   * Handle input changes
   */
  onChange() {
    this.toggleResetButton();

    const newSearchTerm = this.getQuery();

    // Clear results if search term changes significantly
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      const resultsWrapper = this.querySelector("#predictive-search-results-groups-wrapper");
      if (resultsWrapper) {
        // Add fade-out animation before removing
        resultsWrapper.style.animation = 'fadeOut 0.2s ease-out';
        setTimeout(() => {
          resultsWrapper.remove();
        }, 200);
      }
      this.querySelector(".predictive-search__recommendations-wrapper")?.classList.toggle('hidden');
    }

    // Update the search term in the UI
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    this.getSearchResults(this.searchTerm);
  }

  /**
   * Handle form submission
   */
  onFormSubmit(event) {
    if (!this.enableFormSubmit) {
      event.preventDefault();
      return;
    }
    // Check for redirect URL and use it if available
    if (this.redirectUrl) {
      event.preventDefault();
      window.location.href = this.redirectUrl;
      return;
    }

    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  /**
   * Handle form reset
   */
  onFormReset(event) {
    event.preventDefault();

    const isInlineSearch = this.input?.form?.classList.contains('inline-search');

    if (isInlineSearch) {
      this.input.value = '';
      this.toggleResetButton();
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.close(true);
      this.input?.blur();
      return;
    }

    // Fade out current results before clearing
    const resultLists = this.querySelectorAll('.predictive-search__results-list');
    resultLists.forEach(list => {
      list.style.animation = 'fadeOut 0.2s ease-out';
      list.style.opacity = '0';
    });

    // Reset everything after animation
    setTimeout(() => {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
      this.searchTerm = '';

      // Cancel any pending requests
      this.abortController.abort();
      this.abortController = new AbortController();

      // Reset opacity for next search
      resultLists.forEach(list => {
        list.style.opacity = '';
        list.style.animation = '';
      });

      this.getSearchResults(this.searchTerm);
    }, 200);
  }

  /**
   * Handle input focus
   */
  onFocus() {
    this.toggleResetButton();
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from another input, treat as user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  /**
   * Close results when clicking outside the component
   */
  onDocumentClick(event) {
    if (!this.isOpen) return;
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  onInlineClose(event) {
    event.preventDefault();
    this.close();
    this.inlineCloseButton?.blur();
    this.input?.blur();
    if (document.activeElement && this.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  }

  onInlineClosePointerDown(event) {
    event.preventDefault();
    event.stopPropagation();
    this.onInlineClose(event);
  }

  /**
   * Handle keyup for arrow navigation and Enter
   */
  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        this.input?.blur();
        break;
    }
  }

  /**
   * Handle keydown to prevent cursor movement in input
   */
  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  /**
   * Update the search-for text with the new search term
   */
  updateSearchForTerm(previousTerm, newTerm) {
    if (previousTerm === newTerm) return;
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    if (!searchForTextElement) return;
    const searchPrefix = searchForTextElement.dataset.predictiveSearchSearchForText || '';
    searchForTextElement.textContent = `${searchPrefix} "${newTerm}"`;
    searchForTextElement.setAttribute('aria-label', `${searchPrefix} ${newTerm}`);
  }

  /**
   * Navigate between search results
   */
  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Get all visible interactive elements
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter(element => element.offsetParent !== null);

    if (allVisibleElements.length === 0) return;
    if (moveUp && !selectedElement) return;

    let selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    let activeElementIndex = 0;

    // Calculate the next active index
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1
        ? 0
        : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0
        ? allVisibleElements.length - 1
        : selectedElementIndex - 1;
    }

    // Don't do anything if index didn't change
    if (activeElementIndex === selectedElementIndex) return;

    // Update ARIA states
    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);

    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', false);
    }

    // Set the active descendant for accessibility
    this.input.setAttribute('aria-activedescendant', activeElement.id);

    // Clear status text
    if (this.statusElement) {
      this.statusElement.textContent = "";
    }
  }

  /**
   * Select the currently highlighted option
   */
  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  /**
   * Get search results from API or cache
   */
  async getSearchResults(searchTerm) {
    const queryKey = searchTerm?.replace(/\s+/g, "-").toLowerCase() || '';
    const nostoCacheKey = `nostoAPI-${queryKey}`;

    // Remove cache flag at start - will be set if using cached results
    this.removeAttribute('data-from-cache');

    // Set loading states
    this.setLiveRegionLoadingState();
    this.setLoadingState();

    // Open the results immediately since the structure is pre-rendered
    this.setAttribute('results', true);
    this.open();

    // Hide recommendations when showing search results
    this.querySelector(".predictive-search__recommendations-wrapper")?.classList.toggle('hidden', true);

    // Check if we should skip Nosto due to previous failures
    const skipNostoUntil = sessionStorage.getItem('skipNostoUntil');
    const useNosto = !skipNostoUntil || Date.now() > parseInt(skipNostoUntil);

    // Clear expired skipNostoUntil
    if (skipNostoUntil && Date.now() > parseInt(skipNostoUntil)) {
      sessionStorage.removeItem('skipNostoUntil');
    }

    // Check cache first
    if (this.cachedResults[nostoCacheKey]) {
      // Mark that results are from cache to skip animation
      this.setAttribute('data-from-cache', 'true');
      this.renderNostoResults(this.cachedResults[nostoCacheKey], searchTerm);
      return;
    }

    // If we should use Nosto, fetch and render results
    if (useNosto) {
      try {
        const results = await this.getNostoSearchResults(searchTerm);

        // Store redirect URL if it exists in the response
        if (results && results.redirect) {
          // Remove "www." from redirect URL using URL API for robustness
          try {
            const urlObj = new URL(results.redirect, window.location.origin);
            if (urlObj.hostname.startsWith('www.')) {
              urlObj.hostname = urlObj.hostname.slice(4);
            }
            this.redirectUrl = urlObj.toString();
          } catch (e) {
            // Fallback to original redirect if URL parsing fails
            this.redirectUrl = results.redirect;
          }
        } else {
          this.redirectUrl = null;
        }

        if (!results || results.status) {
          throw new Error('Invalid Nosto response');
        }

        // Allow submitting form with Enter key
        this.enableFormSubmit = true;
        // Cache and render Nosto results
        this.cachedResults[nostoCacheKey] = results;
        this.renderNostoResults(results, searchTerm);

        // Reset fallback attempts on success
        sessionStorage.removeItem('fallbackAttempts');

      } catch (nostoError) {
        console.error("Nosto search error:", nostoError);

        // Get current attempt count from sessionStorage
        const currentAttempts = parseInt(sessionStorage.getItem('fallbackAttempts') || '0');
        const newAttempts = currentAttempts + 1;
        const maxRetries = this.maxRetries;

        // Store updated attempt count
        sessionStorage.setItem('fallbackAttempts', String(newAttempts));

        console.log(`Nosto API attempt ${newAttempts} of ${maxRetries} failed`);

        if (newAttempts >= maxRetries) {
          console.log('Max attempts reached, setting skipNostoUntil');
          // Set fallback timeout (10 minutes) in sessionStorage
          sessionStorage.setItem('skipNostoUntil', String(Date.now() + 600000));
        }

        // Continue with Shopify fallback for this request
        await this.fetchSearchResultsSection('predictive-search', searchTerm, `predictive-search-${queryKey}`);
        this.setLiveRegionResults();
      }
    } else {
      // Use Shopify fallback
      try {
        await this.fetchSearchResultsSection('predictive-search', searchTerm, `predictive-search-${queryKey}`);
        this.setLiveRegionResults();
      } catch (error) {
        // Ignore aborted requests
        if (error?.name === 'AbortError' || error?.code === 20) {
          return;
        }

        console.error('Search error:', error);
        this.close();
        this.removeLoadingState();
      }
    }
  }

  async fetchSearchResultsSection(sectionId, searchTerm, cacheKey) {
    // Create a new abort controller for this request
    this.abortController = new AbortController();

    // Fetch HTML section from Shopify
    const response = await fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=${sectionId}`,
      { signal: this.abortController.signal }
    );

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }

    const text = await response.text();
    const resultsElement = new DOMParser()
      .parseFromString(text, 'text/html')
      .querySelector(`#shopify-section-${sectionId}`);

    if (!resultsElement) {
      throw new Error('Could not find results in response');
    }

    const resultsMarkup = resultsElement.innerHTML;

    // Sync cache across all instances
    this.cachedResults[cacheKey] = resultsMarkup;

    // Render the search results section. For Nosto, this will render the initial skeleton loaders
    this.renderSearchResultsSection(resultsMarkup);
  }

  /**
   * Fetch search results from Nosto API
   */
  async getNostoSearchResults(searchTerm) {
    if (!window.nostojs) {
      throw new Error('Nosto API not available');
    }

    const searchParams = !searchTerm || searchTerm === '' ? {
      popularSearches: {
        fields: ["query"],
        size: 5,
        emptyQueryMatchesAll: true
      }
    } : {
      query: searchTerm,
      ...(this.showCategories ? {
        categories: {
          fields: ["name", "urlPath"],
          size: 3
        }
      } : {}),
      products: {
        fields: ['name', 'thumbUrl', 'url', 'brand'],
        size: 5,
        from: 0
      },
      keywords: {
        fields: ['keyword'],
        size: this.maxKeywords
      }
    };
    const trackParams = {
      redirect: false,
      track: 'autocomplete'
    };

    return new Promise(resolve => {
      nostojs(async api => {
        const results = api.search(searchParams, trackParams);
        resolve(results);
      });
    });
  }
  /**
   * Render search results from Nosto
   */
  async renderNostoResults(data, searchTerm) {
    if (!data) return;

    const products = data?.products?.hits || [];
    let keywords = (data?.keywords?.hits || [])?.map(hit => hit.keyword);
    const categories = data?.categories?.hits || [];
    const popularSearches = (data?.popularSearches?.hits || [])?.map(hit => hit.query);
    const totalProducts = data.products?.total || 0;
    const totalKeywords = data.keywords?.total || 0;
    const totalCategories = data.categories?.total || 0;
    if ((!searchTerm || searchTerm === '') && keywords.length === 0 && popularSearches.length > 0) {
      keywords.push(...popularSearches);
    }
    // Limit keywords to maxKeywords count
    if (keywords.length > this.maxKeywords) {
      keywords = keywords.slice(0, this.maxKeywords);
    }

    // Check if search term is empty or undefined
    const isEmptySearch = !searchTerm || searchTerm === '';

    // Generate markup for each section
    const productsMarkup = products.length > 0 ? await this.parseProducts(products) : '';
    const keywordsMarkup = keywords.length > 0 ? await this.parseKeywords(keywords, searchTerm) : '';
    const categoriesMarkup = this.showCategories && categories.length > 0 ? await this.parseCategories(categories) : '';
    const liveRegionMarkup = await this.parseLiveRegionMarkup(totalProducts, totalKeywords, searchTerm);

    // Find the containers
    const keyWordsList = this.querySelector('#predictive-search-results-queries-list');
    const categoriesList = this.querySelector('#predictive-search-results-categories-list');
    const productsList = this.querySelector('#predictive-search-results-products-list');
    const liveRegionCountValue = this.querySelector('[data-predictive-search-live-region-count-value]');
    const productsSection = this.querySelector('#predictive-search-products');
    const categoriesSection = this.querySelector('#predictive-search-categories');
    const keywordsSection = this.querySelector('#predictive-search-option-search-keywords');
    const recommendationsWrapper = this.querySelector('.predictive-search__recommendations-wrapper');

    // Show/hide sections based on whether search term is empty
    if (productsSection) productsSection.classList.toggle('hidden', isEmptySearch);
    if (productsList) productsList.classList.toggle('hidden', isEmptySearch);
    if (categoriesSection) categoriesSection.classList.toggle('hidden', isEmptySearch);
    if (categoriesList) categoriesList.classList.toggle('hidden', isEmptySearch);
    if (keywordsSection) keywordsSection.classList.toggle('hidden', isEmptySearch);
    if (recommendationsWrapper) recommendationsWrapper.classList.toggle('hidden', !isEmptySearch);

    // Update the containers if they exist
    if (keyWordsList && keywordsMarkup) {
      keyWordsList.innerHTML = keywordsMarkup;
    }

    if (this.showCategories && categoriesList && categoriesMarkup) {
      categoriesList.innerHTML = categoriesMarkup;
    }

    if (productsList && productsMarkup) {
      productsList.innerHTML = productsMarkup;
    }

    if (liveRegionCountValue && liveRegionMarkup) {
      liveRegionCountValue.innerHTML = liveRegionMarkup;
    }

    // Set up click tracking for product cards AFTER the DOM has been updated
    this.setupClickTracking();

    // Remove loading state with slight delay to ensure smooth transition
    setTimeout(() => {
      this.removeLoadingState();
    }, 50);
  }

  /**
   * Generate HTML for product results
   */
  async parseProducts(products) {
    if (!products || products.length === 0) return '';

    return products.map((product, index) => `
        <li
          id="predictive-search-option-product-${index + 1}"
          class="predictive-search__list-item"
          role="option"
          aria-selected="false"
        >
          <a
            href="${product.url}"
            class="predictive-search__item predictive-search__item--link-with-thumbnail"
            data-product-id="${product.productId}"
          >
            ${product.thumbUrl ? `
              <img
                class="predictive-search__image"
                src="${product.thumbUrl}"
                alt="${product.name}"
                loading="lazy"
                width="50"
                height="50"
              >
            ` : ''}

            <div class="predictive-search__item-content">
              ${product.brand ? `
                <p><strong>${product.brand}</strong></p>
              ` : ''}
              <p>${product.name}</p>
            </div>
          </a>
        </li>
      `).join('');
  }

  /**
   * Generate HTML for keyword suggestions
   */
  async parseKeywords(keywords, searchTerm) {
    if (!keywords || keywords.length === 0) return '';

    const searchPath = window.routes?.search_url + '?q=' || '/search?q=';

    return keywords.map((keyword, index) => {
      // Use redirect URL for the matching keyword item if available
      const href = (searchTerm?.toLowerCase() === keyword.toLowerCase() && this.redirectUrl) ?
        this.redirectUrl :
        `${searchPath}${encodeURIComponent(keyword)}&keyword=true`;

      return `
        <li
          id="predictive-search-option-query-${index + 1}"
          class="predictive-search__list-item"
          role="option"
          aria-selected="false"
        >
          <a
            href="${href}"
            class="predictive-search__item link"
          >
            <div class="predictive-search__item-content predictive-search__item-content--centered">
              <p
                class="predictive-search__item-heading predictive-search__item-query-result"
                aria-label="${keyword}"
              >
                ${keyword}
              </p>
            </div>
          </a>
        </li>
      `;
    }).join('');
  }

  /**
   * Generate HTML for category results
   */
  async parseCategories(categories) {
    if (!categories || categories.length === 0) return '';

    return categories.map((category, index) => `
        <li
          id="predictive-search-option-category-${index + 1}"
          class="predictive-search__list-item"
          role="option"
          aria-selected="false"
        >
          <a
            href="/collections/${category.urlPath}"
            class="predictive-search__item predictive-search__item--category link"
          >
            <div class="predictive-search__item-content predictive-search__item-content--centered">
              <p
                class="predictive-search__item-heading"
                aria-label="${category.name}"
              >
                ${category.name}
              </p>
            </div>
          </a>
        </li>
      `).join('');
  }

  /**
   * Generate markup for live region (for screen readers)
   */
  async parseLiveRegionMarkup(totalProducts, totalKeywords, searchTerm) {
    // Get localized strings with fallbacks
    const noResultsText = window.nostoStrings?.searchNoResults || 'No results found for "{terms}"';
    const suggestionsText = window.nostoStrings?.searchSuggestionsWithCount || '{count} suggestion(s)';
    const resultsText = window.nostoStrings?.searchResultsWithCount || '{count} result(s)';

    if (totalProducts === 0) {
      return noResultsText.replace('{terms}', searchTerm);
    }

    let markup = '';

    if (totalKeywords > 0) {
      markup += suggestionsText.replace('{count}', totalKeywords) + ' ';
    }

    markup += resultsText.replace('{count}', totalProducts);

    return markup;
  }

  /**
   * Set loading state for live region
   */
  setLiveRegionLoadingState() {
    // Ensure we have a reference to the status element
    if (!this.statusElement) {
      this.statusElement = this.querySelector('.predictive-search-status');
    }

    // Ensure we have loading text
    if (!this.loadingText) {
      this.loadingText = this.getAttribute('data-loading-text') || 'Loading...';
    }

    // Set loading text and state
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  /**
   * Update text in live region for screen readers
   */
  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    // Hide after brief delay to prevent multiple announcements
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  /**
   * Handle the rendering of search results (used for Shopify fallback)
   */
  renderSearchResultsSection(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;

    // Wait for DOM to update before collecting references
    setTimeout(() => {
      // Re-query for skeleton loaders and result lists after content update
      this.skeletonLoaders = this.querySelectorAll('.loading-skeleton-search');
      this.resultLists = this.querySelectorAll('.predictive-search__results-list');

      // Set attribute and open results
      this.setAttribute('results', true);
      this.open();

      this.querySelector(".predictive-search__recommendations-wrapper")?.classList.toggle('hidden', true);

    }, 0);
  }

  /**
   * Set live region results after search completes
   */
  setLiveRegionResults() {
    this.removeAttribute('loading');

    const liveRegionValue = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionValue) {
      this.setLiveRegionText(liveRegionValue.textContent);
    }
  }

  /**
   * Set component into loading state
   */
  setLoadingState() {
    // Important: FIRST show skeletons, THEN set loading attribute
    if (this.skeletonLoaders) {
      this.skeletonLoaders.forEach(loader => {
        if (loader) {
          loader.style.display = 'block';
          loader.setAttribute('aria-hidden', 'false');
        }
      });
    }

    // Hide result lists
    if (this.resultLists) {
      this.resultLists.forEach(list => {
        if (list) list.style.display = 'none';
      });
    }

    // Set attribute last to trigger CSS rules
    this.setAttribute('loading', 'true');
  }

  /**
   * Remove loading state from component
   */
  removeLoadingState() {
    // Hide skeletons
    if (this.skeletonLoaders) {
      this.skeletonLoaders.forEach(loader => {
        if (loader) {
          loader.style.display = 'none';
          loader.setAttribute('aria-hidden', 'true');
        }
      });
    }

    // Show result lists
    if (this.resultLists) {
      this.resultLists.forEach(list => {
        if (list) list.style.display = '';
      });
    }

    // Remove loading attribute
    this.removeAttribute('loading');
  }

  /**
   * Open the search results dropdown
   */
  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.toggleResetButton();
  }

  /**
   * Close the search results dropdown
   */
  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.toggleResetButton();
  }

  /**
   * Close search results and clean up state
   */
  closeResults(clearSearchTerm = false) {
    // Clear search input if requested
    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    // Remove selected state from any selected option
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) {
      selected.setAttribute('aria-selected', false);
    }

    // Reset ARIA state
    if (this.input) {
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', false);
    }

    // Remove attributes and styles
    this.removeAttribute('loading');
    this.removeAttribute('open');

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  /**
   * Toggle visibility of reset button
   */
  toggleResetButton() {
    if (!this.resetButton) return;

    if (this.inlineCloseButton) {
      this.resetButton.classList.remove('hidden');
      return;
    }

    const hasValue = this.input && this.input.value.length > 0;
    // Only show the reset button while the predictive search results are visible.
    const shouldShow = hasValue && this.isOpen;

    this.resetButton.classList.toggle('hidden', !shouldShow);
  }

  /**
   * Debounce helper function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

if (!customElements.get('nosto-autocomplete')) {
  customElements.define('nosto-autocomplete', NostoAutoComplete);
}
